/* eslint-disable max-lines */
import {
	type CollectibleCollectibleAttributeRefined,
	isPwaMode,
	NotificationService,
	PoweredByMws,
	SerieALogo,
	TrackingEventTypeWEB,
	useIntl,
	useLocale,
	UserCredentials,
	useSettings
} from '@capsicum/common';
import {
	Alert,
	Button,
	Divider,
	Image,
	ModalService,
	Snackbar,
	Timer,
	useIdParam,
	useRouter
} from '@capsicum/common-web';
import { InfoIcon, StarIcon } from '@capsicum/common-web/icons';
import {
	type AxiosErrorData,
	type ConfirmDeliveryDrawerProps,
	type SubmitData,
	useAuctionDetails
} from '@capsicum/features/fanshop/shared';
import { AuctionDetailsSkeleton } from '@capsicum/features/fanshop/web';
import { ApprovedFooter } from '@capsicum/features/regulations/web';
import { RequirementsPageCredentialsNeeded } from '@capsicum/socios-core';
import { useCredentialsHandler } from '@capsicum/socios-core-web';
import { useCallback, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useAuctionDetailsTutorial } from '../../hooks';
import {
	AuctionTags,
	BidAmount,
	BidStatusCard,
	ConfirmBid,
	ConfirmDeliveryAddress,
	DeliveryAddress,
	DeliveryCountrySelect,
	LatestBidList,
	ShippingCost,
	TopUp
} from '../../ui';

import * as S from './AuctionDetails.styled';

export const AuctionDetails = ({ params }: { params: { id?: string } }) => {
	const { locale } = useLocale();
	const { $t } = useIntl();
	const { settings } = useSettings();
	const router = useRouter();
	const { id: auctionId } = useIdParam(params, '/fan-shop/rewards');

	const onSuccessfulBid = useCallback(
		() =>
			NotificationService.addNotification({
				component: (
					<Snackbar
						description={$t('rewards.toast.success.message')}
						testID="auctionDetails-successfulBid"
						title={$t('rewards.toast.success.title')}
						variant="success"
					/>
				)
			}),
		[$t]
	);

	const onPendingBid = useCallback(
		() =>
			NotificationService.addNotification({
				component: (
					<Snackbar
						description={$t('rewards.toast.pending.message')}
						testID="auctionDetails-pendingBid"
						title={$t('rewards.toast.pending.title')}
						variant="pending"
					/>
				)
			}),
		[$t]
	);

	const onFailedBid = useCallback(
		(translationCode: string, errorsObj?: Array<AxiosErrorData> | undefined) => {
			if (errorsObj) {
				errorsObj.forEach((error: AxiosErrorData) => {
					const message = error?.translations?.[locale] || error?.translations?.[0];
					const title = $t('rewards.toast.error.errorCode') + error?.status;

					return NotificationService.addNotification({
						component: (
							<Snackbar
								description={message}
								testID="auctionDetails-failedBid"
								title={title.toString()}
								variant="error"
							/>
						)
					});
				});
			} else {
				return NotificationService.addNotification({
					component: (
						<Snackbar
							description={getBidErrorMessage(translationCode)?.message}
							testID="auctionDetails-failedBid"
							title={getBidErrorMessage(translationCode)?.title}
							variant="error"
						/>
					)
				});
			}
		},
		[$t]
	);

	const {
		auctionData,
		auctionEndDate,
		bidListStructureData,
		chzRate,
		chzShortCode,
		chzWalletObj,
		collectibleCosts,
		collectibleCostsLoading,
		conversionRates,
		currentChzBid,
		currentFiatBid,
		deliveryAddressForm,
		deliveryCountry,
		eventName,
		fiatCurrencySymbol,
		fiatShortCode,
		getBidErrorMessage,
		getNumber,
		hasBids,
		isActive,
		isBall,
		isDataLoading,
		isOwnBid,
		isShowSuspendedBanner,
		isShowWinningBanner,
		isSkeletonLoading,
		isTimeUp,
		mainImage,
		minBidFiatAmount,
		pendingBid,
		postDeliveryAddressMutateAsync,
		putCollectibleDeliveryByIdMutateAsync,
		putDeliveryAddressMutateAsync,
		selectedCountryIso2,
		selectedCountryTranslated,
		sendAnalyticsEvent,
		setCurrentStatus,
		setDeliveryAddressData,
		setSelectedCountry,
		setUserBidAmount,
		shippingCostData,
		shippingFiatAmount,
		sortedTags,
		submit,
		texts,
		userBidAmount,
		winningBid
	} = useAuctionDetails(auctionId as string, {
		onFailedBid,
		onPendingBid,
		onSuccessfulBid
	});

	const openConfirmDeliveryAddressDrawer = useCallback(
		({ city = '', country = '', id, street = '', zipCode = '' }: ConfirmDeliveryDrawerProps) => {
			ModalService.addModal({
				renderComponent: ({ close }) => (
					<ConfirmDeliveryAddress
						address={street || ''}
						city={city || ''}
						country={country || ''}
						privacyUrl={settings?.privacyPolicyLink || ''}
						testID="auction-details-confirm-delivery-address-drawer"
						zipCode={zipCode || ''}
						onEdit={() => {
							close();
							openDeliveryAddressDrawer();
						}}
						onPress={() => {
							close();
							putCollectibleDeliveryByIdMutateAsync({
								deliveryAddressData: { deliveryAddressId: id },
								deliveryId: winningBid!.delivery!.id!
							});
						}}
					/>
				)
			});
		},
		[putCollectibleDeliveryByIdMutateAsync, settings?.privacyPolicyLink, winningBid]
	);

	const saveDeliveryAddress = useCallback(
		(data: SubmitData) => {
			setDeliveryAddressData(data);
			if (deliveryAddressForm.current.id) {
				putDeliveryAddressMutateAsync(
					{
						deliveryAddressData: {
							...data,
							isDefault: false
						},
						deliveryAddressId: deliveryAddressForm.current.id
					},
					{
						onSuccess: data => {
							if (data?.data) {
								const { city, country, id, street, zipCode } = setDeliveryAddressData(data.data);

								openConfirmDeliveryAddressDrawer({ city, country, id: id!, street, zipCode });
							}
						}
					}
				);

				return;
			}
			postDeliveryAddressMutateAsync(
				{
					deliveryAddressData: data
				},
				{
					onSuccess: data => {
						if (data?.data) {
							const { city, country, id, street, zipCode } = setDeliveryAddressData(data.data);

							openConfirmDeliveryAddressDrawer({ city, country, id: id!, street, zipCode });
						}
					}
				}
			);
		},
		[
			deliveryAddressForm,
			openConfirmDeliveryAddressDrawer,
			postDeliveryAddressMutateAsync,
			putDeliveryAddressMutateAsync,
			setDeliveryAddressData
		]
	);

	const openDeliveryAddressDrawer = useCallback(() => {
		const formData = deliveryAddressForm?.current || {};

		ModalService.addModal({
			renderComponent: ({ close }) => (
				<DeliveryAddress
					closeModal={close}
					country={deliveryCountry}
					testID="auction-details-delivery-address-modal"
					onClick={saveDeliveryAddress}
					{...formData}
				/>
			)
		});
	}, [deliveryAddressForm, deliveryCountry, saveDeliveryAddress]);

	const openShippingCountryDrawer = useCallback(async () => {
		ModalService.addModal({
			renderComponent: ({ close }) => (
				<DeliveryCountrySelect
					iso2Country={selectedCountryIso2}
					onChange={(country: string) => {
						setSelectedCountry(country);
						close();
					}}
					onClose={close}
				/>
			)
		});
	}, [setSelectedCountry, selectedCountryIso2]);

	const isDisplayButton = !!(auctionData?.status === 'opened' && !isTimeUp && shippingCostData);

	const { placeBidRef } = useAuctionDetailsTutorial(!isDataLoading && isDisplayButton);

	const showTopUpModal = (userFiatAmount: number, amountNeeded: number) => {
		ModalService.addModal({
			renderComponent: ({ close }) => (
				<TopUp
					amountFiatNeeded={amountNeeded}
					chzRate={chzRate}
					closeModal={close}
					fiatCurrencyShortCode={fiatShortCode}
					testID="auction-details-top-up-drawer"
					userAmount={userFiatAmount}
					onButtonPressed={() => {
						router.push('/wallet/topup');
					}}
				/>
			)
		});
	};

	const onBidButtonPressContinue = () => {
		const userFiatAmount = +chzWalletObj!.balance! * chzRate;
		const amountNeeded = minBidFiatAmount + shippingFiatAmount;

		if (userFiatAmount < amountNeeded) {
			showTopUpModal(userFiatAmount, amountNeeded);

			return;
		}
		openBidAmountDrawer();
	};

	const { handleCredential: onBidButtonPress } = useCredentialsHandler({
		credentialCodes: UserCredentials.AUCTIONS_BID,
		onCredentialGranted: () => {
			onBidButtonPressContinue();
			sendAnalyticsEvent(TrackingEventTypeWEB.track, {
				chz_amount: parseFloat((minBidFiatAmount! / chzRate).toFixed(2)),
				fiat_amount: minBidFiatAmount,
				is_pwa_mode: isPwaMode(),
				page: 'Product Details',
				service_fee:
					getNumber(collectibleCosts?.vat?.mainCurrency?.amount) +
					getNumber(collectibleCosts?.buyersPremium?.altCurrency?.amount) +
					getNumber(collectibleCosts?.estimatedShippingCost?.altCurrency?.amount)
			});
		},
		page: RequirementsPageCredentialsNeeded.auctionBid
	});

	const openConfirmBidDrawer = useCallback(
		async (bidAmount: number) => {
			let showPrevModal = true;

			ModalService.addModal({
				isClosable: true,
				onDismiss: () => {
					if (showPrevModal) {
						openBidAmountDrawer(bidAmount);
						setUserBidAmount(0);
					}
				},
				renderComponent: ({ close }) => (
					<ConfirmBid
						bidAmountChz={getNumber(collectibleCosts?.bidAmount?.mainCurrency?.amount)}
						bidAmountFiat={getNumber(collectibleCosts?.bidAmount?.altCurrency?.amount)}
						buyersPremiumChz={getNumber(collectibleCosts?.buyersPremium?.mainCurrency?.amount)}
						buyersPremiumFiat={getNumber(collectibleCosts?.buyersPremium?.altCurrency?.amount)}
						buyersPremiumPercentage={getNumber(collectibleCosts?.buyersPremiumPercentage)}
						chzCurrencyShortcode={chzShortCode}
						closeModal={close}
						country={selectedCountryTranslated}
						estimatedShippingCostChz={getNumber(
							collectibleCosts?.estimatedShippingCost?.mainCurrency?.amount
						)}
						estimatedShippingCostFiat={getNumber(
							collectibleCosts?.estimatedShippingCost?.altCurrency?.amount
						)}
						fiatCurrencyShortcode={fiatShortCode}
						testID="confirm-bid-drawer"
						totalChz={getNumber(collectibleCosts?.totalPrice?.mainCurrency?.amount)}
						totalFiat={getNumber(collectibleCosts?.totalPrice?.altCurrency?.amount)}
						vatChz={getNumber(collectibleCosts?.vat?.mainCurrency?.amount)}
						vatFiat={getNumber(collectibleCosts?.vat?.altCurrency?.amount)}
						onBidSubmit={() => {
							showPrevModal = false;

							sendAnalyticsEvent(TrackingEventTypeWEB.track, {
								chz_amount: getNumber(collectibleCosts?.bidAmount?.mainCurrency?.amount),
								fiat_amount: getNumber(collectibleCosts?.bidAmount?.altCurrency?.amount),
								is_pwa_mode: isPwaMode(),
								page: 'Confirm your bid and country',
								service_fee:
									getNumber(collectibleCosts?.vat?.mainCurrency?.amount) +
									getNumber(collectibleCosts?.buyersPremium?.altCurrency?.amount) +
									getNumber(collectibleCosts?.estimatedShippingCost?.altCurrency?.amount)
							});

							return submit();
						}}
					/>
				),
				testID: 'auction-confirm-bid'
			});
		},
		[conversionRates, selectedCountryTranslated, submit, collectibleCosts]
	);

	const openBidAmountDrawer = useCallback(
		(bidAmount?: number) => {
			ModalService.clearModals();
			ModalService.addModal({
				isClosable: true,
				renderComponent: ({ close }) => (
					<BidAmount
						chzCurrencyShortCode={chzShortCode}
						chzRate={chzRate}
						closeModal={close}
						country={selectedCountryTranslated}
						initialBidAmount={bidAmount || minBidFiatAmount}
						mainCurrencyShortcode={fiatShortCode}
						mainCurrencySymbol={fiatCurrencySymbol}
						minimumBid={minBidFiatAmount}
						shippingCost={shippingFiatAmount}
						testID="bid-amount-drawer"
						userAmount={+chzWalletObj!.balance! * chzRate}
						onPress={currentBidAmount => {
							setUserBidAmount(currentBidAmount);
							sendAnalyticsEvent(TrackingEventTypeWEB.track, {
								chz_amount: parseFloat((minBidFiatAmount! / chzRate).toFixed(2)),
								fiat_amount: currentBidAmount,
								is_pwa_mode: isPwaMode(),
								page: 'Enter your bidding amount',
								service_fee:
									getNumber(collectibleCosts?.vat?.mainCurrency?.amount) +
									getNumber(collectibleCosts?.buyersPremium?.altCurrency?.amount) +
									getNumber(collectibleCosts?.estimatedShippingCost?.altCurrency?.amount)
							});
						}}
					/>
				)
			});
		},
		[conversionRates, selectedCountryTranslated, shippingFiatAmount, chzWalletObj, minBidFiatAmount]
	);

	const debouncedConfirmModal = useDebouncedCallback((bid: number) => openConfirmBidDrawer(bid), 500);

	useEffect(() => {
		if (!!collectibleCosts && !!userBidAmount) {
			debouncedConfirmModal(userBidAmount);
		}
	}, [collectibleCosts, userBidAmount]);

	const image = useMemo(
		() =>
			mainImage ? (
				<S.ImageWrapper>
					<S.MainImage alt="image" height={303} src={mainImage} width={256} />
					{!isBall ? (
						<S.PoweredMws alt="MWS logo" height={59} src={PoweredByMws} width={65} />
					) : (
						<S.SerieAlogoWrapper>
							<S.PoweredByText>{texts.poweredBy}</S.PoweredByText>
							<Image alt="SerieA logo" height={37} src={SerieALogo} width={25} />
						</S.SerieAlogoWrapper>
					)}
				</S.ImageWrapper>
			) : (
				<S.StyledCardImagePlaceholder />
			),
		[isBall, mainImage, texts.poweredBy]
	);

	const eventContainer = useMemo(
		() => (
			<S.EventContainer>
				<S.Event>{eventName}</S.Event>
				{isBall && <S.GoalBy>{$t('rewards.auctions.goalBy')}</S.GoalBy>}
				<S.Player>{auctionData?.collectible?.name}</S.Player>
			</S.EventContainer>
		),
		[$t, auctionData?.collectible?.name, eventName, isBall]
	);

	const timer = useMemo(
		() => (
			<Timer
				customFormat="auction"
				customStatus="closed"
				expirationDate={auctionEndDate}
				type="default"
				onElapsed={() => setCurrentStatus('closed')}
			/>
		),
		[auctionEndDate, setCurrentStatus]
	);

	const auctionTags = useMemo(
		() => (
			<S.AuctionTagsContainer>
				<AuctionTags auctionData={auctionData} hasIcons isBall={isBall} sortedTags={sortedTags} />
			</S.AuctionTagsContainer>
		),
		[auctionData, isBall, sortedTags]
	);

	const bidStatus = useMemo(
		() => (
			<BidStatusCard
				chzRate={chzRate}
				chzShortCode={chzShortCode}
				fiatShortCode={fiatShortCode}
				isActive={isActive}
				isOwnBid={isOwnBid}
				priceCurrentBid={currentFiatBid}
				priceCurrentBidChz={currentChzBid}
				priceNextBid={minBidFiatAmount}
			/>
		),
		[chzRate, chzShortCode, fiatShortCode, isActive, isOwnBid, currentFiatBid, minBidFiatAmount, currentChzBid]
	);

	const suspendedBanner = useMemo(
		() =>
			isShowSuspendedBanner && (
				<Alert
					closable={false}
					icon={InfoIcon}
					subTitle={$t('rewards.auctions.suspendedBanner.text')}
					testID="auction-suspended-banner"
					title={$t('rewards.auctions.suspendedBanner.title')}
					type="important"
				/>
			),
		[$t, isShowSuspendedBanner]
	);

	const shippingCost = useMemo(
		() =>
			isActive && (
				<ShippingCost
					chzCurrencyShortCode={chzShortCode}
					chzRate={chzRate}
					fiatCurrencyShortCode={fiatShortCode}
					iso2Country={selectedCountryIso2}
					shippingCost={shippingFiatAmount}
					testID="auction-shipping-cost"
					onClick={openShippingCountryDrawer}
				/>
			),
		[
			chzRate,
			chzShortCode,
			fiatShortCode,
			isActive,
			openShippingCountryDrawer,
			selectedCountryIso2,
			shippingFiatAmount
		]
	);

	const winnerBanner = useMemo(
		() =>
			isShowWinningBanner && (
				<Alert
					closable={false}
					icon={StarIcon}
					subTitle={$t('rewards.auctions.winnerBanner.text')}
					testID="auction-winning-banner"
					title={$t('rewards.auctions.winnerBanner.title')}
					type="message"
					onClick={openDeliveryAddressDrawer}
				>
					<S.WinningBannerDeliveryText>
						{$t('rewards.auctions.winnerBanner.delivery')}
					</S.WinningBannerDeliveryText>
				</Alert>
			),
		[$t, isShowWinningBanner]
	);

	const latestBidList = useMemo(
		() =>
			hasBids && (
				<>
					<LatestBidList
						auctionId={auctionId as string}
						bidParticipants={bidListStructureData}
						chzCurrencyShortCode={chzShortCode}
						chzRate={chzRate}
						fiatCurrencyShortCode={fiatShortCode}
					/>
					<Divider orientation="horizontal" />
				</>
			),
		[auctionId, bidListStructureData, chzRate, chzShortCode, fiatShortCode, hasBids]
	);

	const description = useMemo(
		() => (
			<S.DescriptionWrapper>
				<S.SectionTitle>{texts.aboutThisProduct}</S.SectionTitle>
				<S.DescriptionItemsContainer>
					{auctionData?.collectible?.collectibleAttributes?.map(
						(item: CollectibleCollectibleAttributeRefined, index: number) => {
							if (item.attribute?.isVisible) {
								return (
									<S.DescriptionItem key={index}>
										<S.DescriptionItemTitle>{item.attribute.name}</S.DescriptionItemTitle>
										<S.DescriptionItemValue>{item.value}</S.DescriptionItemValue>
									</S.DescriptionItem>
								);
							}

							return null;
						}
					)}
				</S.DescriptionItemsContainer>
			</S.DescriptionWrapper>
		),
		[auctionData?.collectible?.collectibleAttributes, texts.aboutThisProduct]
	);

	const shippingDetailes = useMemo(
		() => (
			<S.ShippingDetailsContainer>
				<S.SectionTitle>{$t('rewards.auctions.shippingTitle')}</S.SectionTitle>
				<S.ShippingDetailsText>{$t('rewards.auctions.shippingText')}</S.ShippingDetailsText>
			</S.ShippingDetailsContainer>
		),
		[$t]
	);

	const button = useMemo(
		() =>
			isDisplayButton && (
				<S.ButtonWrapper>
					<Button
						color="brand"
						disabled={collectibleCostsLoading || pendingBid}
						isLoading={pendingBid}
						ref={settings.areTutorialsEnabled.auctionDetails ? placeBidRef : undefined}
						testID="auction-button-redeem"
						text={texts.placeABid}
						onClick={onBidButtonPress}
					/>
				</S.ButtonWrapper>
			),
		[
			collectibleCostsLoading,
			isDisplayButton,
			onBidButtonPress,
			pendingBid,
			placeBidRef,
			settings.areTutorialsEnabled.auctionDetails,
			texts.placeABid
		]
	);

	return (
		<S.Wrapper $isDisplayButton={isDisplayButton}>
			<S.AuctionUkHeaderWarning page="auction"></S.AuctionUkHeaderWarning>
			{isSkeletonLoading ? (
				<AuctionDetailsSkeleton />
			) : (
				<>
					<S.Title>{$t('rewards.fanshop.product.title')}</S.Title>
					<S.MainContainer>
						{image}
						<S.Content>
							<S.HeaderWrapper>
								{eventContainer}
								{timer}
							</S.HeaderWrapper>
							{auctionTags}
							{bidStatus}
							{suspendedBanner}
							{shippingCost}
							{winnerBanner}
							<Divider orientation="horizontal" />
							{latestBidList}
							{description}
							<Divider orientation="horizontal" />
							{shippingDetailes}
							{button}
						</S.Content>
					</S.MainContainer>
				</>
			)}
			<ApprovedFooter page="auction" />
		</S.Wrapper>
	);
};
/* eslint-enable max-lines */
